import React from 'react';
import { Link } from 'react-router-dom';

const serviceCategories = [
  {
    title: 'Hair',
    description:
      'Cuts, styling, colour, and treatment journeys supported by provider discovery and future AI-led inspiration.',
  },
  {
    title: 'Beard',
    description:
      'Barbering, shaping, grooming, and tailored look recommendations for modern men’s care journeys.',
  },
  {
    title: 'Nails',
    description:
      'Everyday, occasion, and design-led nail services with future category-wise browsing and provider comparison.',
  },
  {
    title: 'Tattoo',
    description:
      'Tattoo discovery, artist visibility, and portfolio-led matching for style-aware decision making.',
  },
  {
    title: 'Piercing',
    description:
      'Trusted provider discovery for piercing services, aftercare awareness, and safe appointment planning.',
  },
  {
    title: 'Beauty',
    description:
      'A broader beauty-services layer that can grow with HnB’s platform, provider, and ERP-ready model.',
  },
];

const highlights = [
  'Belfast-first beauty and personal services platform',
  'Provider discovery with future AI-supported style journeys',
  'Booking, payments, and operational workflows designed to scale',
];

const howItWorks = [
  {
    step: '01',
    title: 'Discover',
    body: 'Browse service categories, compare providers, and explore the platform’s structured public experience.',
  },
  {
    step: '02',
    title: 'Try and shortlist',
    body: 'Move into AI-supported style exploration, understand plan options, and shortlist providers that match the look you want.',
  },
  {
    step: '03',
    title: 'Book with confidence',
    body: 'Progress into booking, payments, and trusted provider workflows as the HnB platform expands.',
  },
];

const trustPoints = [
  {
    title: 'Built for customers',
    body: 'HnB is designed to make local service discovery, comparison, and booking easier to understand and easier to trust.',
  },
  {
    title: 'Built for providers',
    body: 'The platform is structured to support provider visibility, portfolio marketing, bookings, and future operational reporting.',
  },
  {
    title: 'Built to scale',
    body: 'The current prototype is being shaped into a larger ERP-ready platform with clear role separation, governance, and future AI integration.',
  },
];

const publicPages = [
  { label: 'Services', to: '/services' },
  { label: 'Find a provider', to: '/find-provider' },
  { label: 'AI plans', to: '/ai-plans' },
  { label: 'Provider plans', to: '/provider-plans' },
  { label: 'About', to: '/about' },
  { label: 'Downloads', to: '/downloads' },
];

export default function HomePage() {
  return (
    <main
      style={{
        background: '#f8fafc',
        color: '#0f172a',
        minHeight: '100vh',
      }}
    >
      <section
        style={{
          padding: '72px 24px 56px',
          borderBottom: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            maxWidth: '1180px',
            margin: '0 auto',
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
            gap: '32px',
            alignItems: 'center',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <p
              style={{
                margin: 0,
                color: '#4f46e5',
                fontWeight: 700,
                letterSpacing: '0.05em',
                textTransform: 'uppercase',
                fontSize: '0.9rem',
              }}
            >
              HnB public platform
            </p>

            <h1
              style={{
                margin: 0,
                fontSize: '3.25rem',
                lineHeight: 1.05,
                color: '#0f172a',
              }}
            >
              Discover trusted beauty and personal care services with HnB
            </h1>

            <p
              style={{
                margin: 0,
                color: '#475569',
                fontSize: '1.08rem',
                lineHeight: 1.8,
                maxWidth: '62ch',
              }}
            >
              Handsome & Beautiful is being built as a Belfast-first platform for
              service discovery, AI-supported style exploration, booking, and provider
              growth across hair, beard, nails, tattoo, piercing, and broader beauty
              categories.
            </p>

            <div
              style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap',
              }}
            >
              <Link
                to="/find-provider"
                style={{
                  textDecoration: 'none',
                  background: '#111827',
                  color: '#ffffff',
                  padding: '13px 18px',
                  borderRadius: '10px',
                  fontWeight: 600,
                }}
              >
                Find a provider
              </Link>

              <Link
                to="/ai-plans"
                style={{
                  textDecoration: 'none',
                  background: '#ffffff',
                  color: '#111827',
                  padding: '13px 18px',
                  borderRadius: '10px',
                  fontWeight: 600,
                  border: '1px solid #d1d5db',
                }}
              >
                View AI plans
              </Link>

              <Link
                to="/services"
                style={{
                  textDecoration: 'none',
                  background: '#eef2ff',
                  color: '#4338ca',
                  padding: '13px 18px',
                  borderRadius: '10px',
                  fontWeight: 600,
                  border: '1px solid #c7d2fe',
                }}
              >
                Explore services
              </Link>
            </div>
          </div>

          <div
            style={{
              background: '#ffffff',
              border: '1px solid #e5e7eb',
              borderRadius: '24px',
              padding: '28px',
              display: 'flex',
              flexDirection: 'column',
              gap: '18px',
              boxShadow: '0 10px 30px rgba(15, 23, 42, 0.06)',
            }}
          >
            <div
              style={{
                display: 'inline-flex',
                alignSelf: 'flex-start',
                padding: '8px 12px',
                borderRadius: '999px',
                background: '#eef2ff',
                color: '#4338ca',
                fontWeight: 700,
                fontSize: '0.9rem',
              }}
            >
              Public experience
            </div>

            <h2 style={{ margin: 0, fontSize: '1.45rem', color: '#111827' }}>
              More than a simple listing site
            </h2>

            <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.7 }}>
              HnB is being shaped to connect provider discovery, trust, future AI
              try-on, booking journeys, and operational structure in one consistent
              customer-facing experience.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {highlights.map((item) => (
                <div
                  key={item}
                  style={{
                    display: 'flex',
                    gap: '12px',
                    alignItems: 'flex-start',
                    padding: '12px 14px',
                    borderRadius: '14px',
                    background: '#f8fafc',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  <div
                    style={{
                      width: '10px',
                      height: '10px',
                      marginTop: '6px',
                      borderRadius: '999px',
                      background: '#4f46e5',
                      flexShrink: 0,
                    }}
                  />
                  <p style={{ margin: 0, color: '#334155', lineHeight: 1.6 }}>{item}</p>
                </div>
              ))}
            </div>

            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                gap: '10px',
              }}
            >
              {publicPages.map((page) => (
                <Link
                  key={page.to}
                  to={page.to}
                  style={{
                    textDecoration: 'none',
                    color: '#4f46e5',
                    fontWeight: 600,
                    padding: '10px 12px',
                    borderRadius: '12px',
                    background: '#f8fafc',
                    border: '1px solid #e5e7eb',
                  }}
                >
                  {page.label}
                </Link>
              ))}
            </div>

            <Link
              to="/dashboard"
              style={{
                textDecoration: 'none',
                color: '#4f46e5',
                fontWeight: 600,
              }}
            >
              Open internal dashboard
            </Link>
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 24px' }}>
        <div
          style={{
            maxWidth: '1180px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p
              style={{
                margin: 0,
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.85rem',
              }}
            >
              Services
            </p>

            <h2 style={{ margin: 0, fontSize: '2rem', color: '#111827' }}>
              Service categories that anchor the public experience
            </h2>

            <p style={{ margin: 0, color: '#64748b', lineHeight: 1.7, maxWidth: '72ch' }}>
              These categories reflect the HnB service structure and provide the
              foundation for provider search, stylist discovery, work samples, and
              future booking journeys.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '16px',
            }}
          >
            {serviceCategories.map((category) => (
              <div
                key={category.title}
                style={{
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '18px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#111827' }}>
                  {category.title}
                </h3>
                <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.7 }}>
                  {category.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: '56px 24px',
          background: '#ffffff',
          borderTop: '1px solid #e5e7eb',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <div
          style={{
            maxWidth: '1180px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p
              style={{
                margin: 0,
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.85rem',
              }}
            >
              How it works
            </p>

            <h2 style={{ margin: 0, fontSize: '2rem', color: '#111827' }}>
              A clearer journey from exploration to booking
            </h2>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
              gap: '16px',
            }}
          >
            {howItWorks.map((item) => (
              <div
                key={item.step}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '18px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                  background: '#ffffff',
                }}
              >
                <span
                  style={{
                    color: '#4338ca',
                    fontWeight: 700,
                    fontSize: '0.95rem',
                  }}
                >
                  {item.step}
                </span>
                <h3 style={{ margin: 0, fontSize: '1.1rem', color: '#111827' }}>
                  {item.title}
                </h3>
                <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.7 }}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section style={{ padding: '56px 24px' }}>
        <div
          style={{
            maxWidth: '1180px',
            margin: '0 auto',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
            <p
              style={{
                margin: 0,
                color: '#4f46e5',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                fontSize: '0.85rem',
              }}
            >
              Why HnB
            </p>

            <h2 style={{ margin: 0, fontSize: '2rem', color: '#111827' }}>
              Trust, structure, and future-ready growth
            </h2>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
              gap: '16px',
            }}
          >
            {trustPoints.map((item) => (
              <div
                key={item.title}
                style={{
                  background: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '18px',
                  padding: '20px',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '10px',
                }}
              >
                <h3 style={{ margin: 0, fontSize: '1.08rem', color: '#111827' }}>
                  {item.title}
                </h3>
                <p style={{ margin: 0, color: '#6b7280', lineHeight: 1.7 }}>
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        style={{
          padding: '64px 24px 72px',
          background: '#0f172a',
          color: '#ffffff',
        }}
      >
        <div
          style={{
            maxWidth: '980px',
            margin: '0 auto',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            gap: '18px',
            alignItems: 'center',
          }}
        >
          <p
            style={{
              margin: 0,
              color: '#a5b4fc',
              fontWeight: 700,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
              fontSize: '0.85rem',
            }}
          >
            Next steps
          </p>

          <h2 style={{ margin: 0, fontSize: '2.2rem', lineHeight: 1.15 }}>
            Explore the public HnB experience as it grows
          </h2>

          <p
            style={{
              margin: 0,
              color: '#cbd5e1',
              lineHeight: 1.8,
              maxWidth: '64ch',
            }}
          >
            Continue into services, compare AI plan options, search for providers,
            or learn more about the wider HnB platform vision.
          </p>

          <div
            style={{
              display: 'flex',
              gap: '12px',
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            <Link
              to="/find-provider"
              style={{
                textDecoration: 'none',
                background: '#ffffff',
                color: '#111827',
                padding: '13px 18px',
                borderRadius: '10px',
                fontWeight: 600,
              }}
            >
              Find a provider
            </Link>

            <Link
              to="/ai-plans"
              style={{
                textDecoration: 'none',
                background: 'transparent',
                color: '#ffffff',
                padding: '13px 18px',
                borderRadius: '10px',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              AI plans
            </Link>

            <Link
              to="/about"
              style={{
                textDecoration: 'none',
                background: 'transparent',
                color: '#ffffff',
                padding: '13px 18px',
                borderRadius: '10px',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              About us
            </Link>

            <Link
              to="/downloads"
              style={{
                textDecoration: 'none',
                background: 'transparent',
                color: '#ffffff',
                padding: '13px 18px',
                borderRadius: '10px',
                fontWeight: 600,
                border: '1px solid rgba(255,255,255,0.2)',
              }}
            >
              Downloads
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
}